import i18n from '../../../locales/i18n';
import { Shop } from '../../../types';
import {
  SalesItems,
  SalesPayments,
  SalesResponse
} from '../../sales/sales.type';
import { escposBuilder, EscPosBuilder } from './escpos';
import { getHeaderSettings, HeaderSettings } from './local-printer.settings';

/**
 * Receipt layout.
 *
 * Turns the sale Simple POS gets back from PUT /Sales into ESC/POS bytes. The
 * connector does this in PrintService.cs; this is the subset a Simple POS
 * sale needs, on the same 42-column grid so the two compare side by side.
 *
 * Adapted from POSUI client/js/shared/serial/receiptlayout.service.js. The
 * differences are the payload: Simple POS receipts carry the item name in
 * parameter2, change as a negative 'Change' payment, and an order number the
 * customer collects against.
 */

// The API may send fields the SalesItems type does not declare.
type ReceiptItem = SalesItems & {
  parameter1?: string;
  parameter2?: string;
  itemgroupName?: string;
};

type ReceiptPayment = SalesPayments & { cardName?: string };

export type ReceiptOptions = {
  isCopy?: boolean;
  shop?: Shop;
};

const label = (key: string, fallback: string) =>
  i18n.t(`LocalPrinter.Receipt.${key}`, { defaultValue: fallback });

const num = (value: unknown) => {
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const two = (n: number) => `${n < 10 ? '0' : ''}${n}`;

const formatDateTime = (date: Date) =>
  `${two(date.getDate())}-${two(date.getMonth() + 1)}-${date.getFullYear()} ` +
  `${two(date.getHours())}:${two(date.getMinutes())}`;

/** Left and right text on one line, padded to width. */
const pad = (left: unknown, right: unknown, width: number) => {
  let l = String(left === undefined || left === null ? '' : left);
  const r = String(right === undefined || right === null ? '' : right);
  if (width - l.length - r.length < 1) {
    // Trim the description rather than push the amount onto a new line.
    l = l.substring(0, Math.max(0, width - r.length - 1));
  }
  const gap = Math.max(1, width - l.length - r.length);
  return l + ' '.repeat(gap) + r;
};

const eachLine = (text: unknown, fn: (line: string) => void) =>
  String(text === undefined || text === null ? '' : text)
    .split(/\r\n|\r|\n/)
    .forEach(fn);

/** Receipt money. ISK has no minor unit, so the POS never shows decimals. */
const moneyFormatter = (currency?: string) => {
  const decimals = currency === 'ISK' ? 0 : 2;
  return (value: unknown) => round2(num(value)).toFixed(decimals);
};

const describe = (item: ReceiptItem) =>
  item.parameter2 ||
  item.parameter1 ||
  item.itemgroupName ||
  item.ean ||
  label('Item', 'Item');

const effectivePrice = (item: ReceiptItem) =>
  num(
    item.effectiveSalesPrice !== undefined && item.effectiveSalesPrice !== null
      ? item.effectiveSalesPrice
      : item.salesPrice
  );

const itemsTotal = (items: ReceiptItem[]) =>
  round2(
    items.reduce(
      (sum, item) => sum + effectivePrice(item) * num(item.quantity),
      0
    )
  );

const paymentName = (p: ReceiptPayment) => {
  const type = p.paymentType || '';
  const name = label(`PaymentTypes.${type}`, type);
  const card = p.cardname || p.cardName;
  return card ? `${name} ${card}` : name;
};

/**
 * Header lines. A field typed on the till wins; a blank one falls back to the
 * shop record Simple POS already loads.
 */
export const resolveHeader = (shop?: Shop, typed = getHeaderSettings()) => {
  const typedLines = (typed.addressLines || []).filter(l => l && l.trim());
  const shopLines = shop
    ? [
        shop.address1,
        shop.address2,
        [shop.zip, shop.city].filter(Boolean).join(' ')
      ].filter((l): l is string => !!l && !!l.trim())
    : [];

  const vat = typed.vatNumber || (shop && shop.CVR) || '';

  const header: HeaderSettings = {
    shopName: typed.shopName || '',
    addressLines: typedLines.length ? typedLines : shopLines,
    vatNumber: vat,
    footer: typed.footer || ''
  };
  return header;
};

// ----------------------------------------------------------------- sections

const printHeader = (b: EscPosBuilder, header: HeaderSettings) => {
  if (!header.shopName && !header.addressLines.length && !header.vatNumber) {
    return;
  }
  b.center();
  if (header.shopName)
    b.large()
      .line(header.shopName)
      .normal();
  header.addressLines.forEach(l => b.line(l));
  if (header.vatNumber) {
    b.line(`${label('VatNumber', 'VAT number')} ${header.vatNumber}`);
  }
  b.left().feed(1);
};

const printCopyBanner = (b: EscPosBuilder) =>
  b
    .center()
    .bold(true)
    .wide()
    .line(`*** ${label('Copy', 'Copy').toUpperCase()} ***`)
    .normal()
    .bold(false)
    .left()
    .feed(1);

const printOrderNumber = (b: EscPosBuilder, orderId?: number) => {
  if (!orderId) return;
  b.center()
    .line(label('Order', 'Order'))
    .large()
    .bold(true)
    .line(String(orderId))
    .bold(false)
    .normal()
    .left()
    .feed(1);
};

const printMeta = (
  b: EscPosBuilder,
  sale: SalesResponse,
  cols: number
): void => {
  const { receipt } = sale;
  const date = receipt.date ? new Date(receipt.date) : new Date();
  b.line(pad(formatDateTime(date), receipt.receiptId || '', cols));

  const pos = receipt.posId !== undefined ? `#${receipt.posId}` : '';
  if (receipt.clerkName) {
    b.line(
      pad(`${label('ServedBy', 'Served by')} ${receipt.clerkName}`, pos, cols)
    );
  } else if (pos) {
    b.line(pad('', pos, cols));
  }

  if (receipt.customerNumber && receipt.customerNumber !== '0') {
    b.line(pad(label('Customer', 'Customer'), receipt.customerNumber, cols));
  }
};

const printItems = (
  b: EscPosBuilder,
  items: ReceiptItem[],
  money: (v: unknown) => string,
  cols: number
) =>
  items.reduce((count, item) => {
    const qty = num(item.quantity);
    const discount = num(item.calculatedDiscount);
    // calculatedDiscount is per unit and effectiveSalesPrice already has it
    // taken off. Show the undiscounted price on the item line so the line and
    // the discount beneath it reconcile to what the customer pays.
    const unit = discount ? num(item.salesPrice) : effectivePrice(item);
    const lineTotal = round2(unit * qty);

    // Bundle contents are listed under the bundle, priced into it.
    if (item.isBundleItem) {
      b.line(`  ${qty !== 1 ? `${qty} x ` : ''}${describe(item)}`);
      return count;
    }

    if (qty !== 1) {
      b.line(describe(item));
      b.line(pad(`  ${qty} x ${money(unit)}`, money(lineTotal), cols));
    } else {
      b.line(pad(describe(item), money(lineTotal), cols));
    }

    if (discount) {
      b.line(
        pad(
          `  ${label('Discount', 'Discount')}`,
          money(-Math.abs(round2(discount * qty))),
          cols
        )
      );
    }

    return count + qty;
  }, 0);

const printTotals = (
  b: EscPosBuilder,
  items: ReceiptItem[],
  payments: ReceiptPayment[],
  money: (v: unknown) => string,
  cols: number,
  itemCount: number
) => {
  const lines = itemsTotal(items);
  // What was actually settled. A basket-level discount is taken off the sale
  // as a whole rather than spread over the item lines, so when payments are
  // present they are the authority on the total and any gap is the discount.
  const settled = payments.length
    ? round2(payments.reduce((sum, p) => sum + num(p.amount), 0))
    : lines;
  const basketDiscount = round2(lines - settled);

  if (Math.abs(basketDiscount) >= 0.01) {
    b.line(
      pad(
        label('TotalDiscount', 'Total discount'),
        money(-basketDiscount),
        cols
      )
    );
  }

  b.bold(true)
    .wide()
    .line(pad(label('Total', 'Total'), money(settled), Math.floor(cols / 2)))
    .normal()
    .bold(false);

  if (itemCount) {
    b.line(pad(label('ItemCount', 'Number of items'), itemCount, cols));
  }
};

const printPayments = (
  b: EscPosBuilder,
  payments: ReceiptPayment[],
  money: (v: unknown) => string,
  cols: number
) => {
  if (!payments.length) return;
  b.rule('-');
  payments.forEach(p => {
    b.line(pad(paymentName(p), money(p.amount), cols));
    const rounding = num(p.rounding);
    if (rounding) {
      b.line(pad(`  ${label('Rounding', 'Rounding')}`, money(rounding), cols));
    }
  });
};

const printVat = (
  b: EscPosBuilder,
  items: ReceiptItem[],
  money: (v: unknown) => string,
  cols: number
) => {
  const groups: { [rate: string]: { net: number; vat: number } } = {};

  items.forEach(item => {
    const rate = num(item.vat);
    if (!rate) return;
    const gross = effectivePrice(item) * num(item.quantity);
    const vat = gross - gross / (1 + rate / 100);
    const group = groups[rate] || { net: 0, vat: 0 };
    group.net += gross - vat;
    group.vat += vat;
    groups[rate] = group;
  });

  const rates = Object.keys(groups).sort((a, c) => Number(a) - Number(c));
  if (!rates.length) return;

  b.rule('-');
  b.line(label('VatIncluded', 'VAT included'));
  rates.forEach(rate => {
    const g = groups[rate];
    const left = `  ${Number(rate)}%   ${money(g.net)}`;
    b.line(pad(left, money(g.vat), cols));
  });
};

const printFooter = (
  b: EscPosBuilder,
  header: HeaderSettings,
  receiptId?: number
) => {
  if (receiptId) {
    b.feed(1)
      .center()
      .barcode39(String(receiptId))
      .left();
  }
  if (header.footer) {
    b.feed(1).center();
    eachLine(header.footer, l => b.line(l));
    b.left();
  }
};

// ------------------------------------------------------------------ builders

export const buildSaleReceipt = (
  sale: SalesResponse,
  options: ReceiptOptions = {}
): Uint8Array => {
  const { receipt } = sale;
  const items = (receipt.items || []) as ReceiptItem[];
  const payments = (receipt.payments || []) as ReceiptPayment[];
  const money = moneyFormatter(receipt.tenantCurrencyCode);
  const header = resolveHeader(options.shop);
  const b = escposBuilder();
  const cols = b.columns;

  b.init();
  printHeader(b, header);
  if (options.isCopy) printCopyBanner(b);
  printOrderNumber(b, sale.orderId);
  printMeta(b, sale, cols);

  b.rule('-');
  const itemCount = printItems(b, items, money, cols);
  b.rule('-');

  printTotals(b, items, payments, money, cols, itemCount);
  printPayments(b, payments, money, cols);
  printVat(b, items, money, cols);
  printFooter(b, header, receipt.receiptId);

  b.feed(4).cut();
  return b.bytes();
};

/** The card terminal's own receipt text, printed verbatim. */
export const buildCardReceipt = (text: string): Uint8Array => {
  const b = escposBuilder();
  b.init().left();
  eachLine(text, l => b.line(l));
  b.feed(4).cut();
  return b.bytes();
};

/** A short self-test slip, for proving the cable and the character table. */
export const buildTestPage = (shop?: Shop): Uint8Array => {
  const b = escposBuilder();
  const cols = b.columns;
  const header = resolveHeader(shop);
  const money = moneyFormatter();

  b.init();
  printHeader(b, header);
  b.center()
    .large()
    .line('TEST')
    .normal()
    .line('Printed from Simple POS')
    .left()
    .feed(1)
    .rule('=');

  b.line(pad('Columns', cols, cols));
  b.line(pad('Date', formatDateTime(new Date()), cols));
  b.rule('=');
  b.line(pad('Sample item', money(49.5), cols));
  b.line(pad(`  2 x ${money(49.5)}`, money(99), cols));
  b.rule('-');
  b.bold(true)
    .line(pad('TOTAL', money(99), cols))
    .bold(false);
  b.feed(1)
    .center()
    .line('æ ø å  Æ Ø Å  £')
    .left();
  if (header.footer) {
    b.feed(1).center();
    eachLine(header.footer, l => b.line(l));
    b.left();
  }
  b.feed(4).cut();
  return b.bytes();
};

// Exposed for byte-comparison work against the connector and unit tests.
export const internals = { pad, itemsTotal, resolveHeader };
